// Paper sizes + Option-B sheet layout math for Print View (see PRINT_VIEW_PLAN.md).
// Pure functions — the geometry is deterministic given the container size, the
// chosen paper, and orientation, so it can be unit-tested and reused by both the
// on-screen frame (Phase 2) and the export crop/compose (Phase 1/4).
// Gaia's set: ISO A0–A4 plus North American Letter / Legal / Tabloid. These do
// NOT all share one aspect ratio, so the frame reshapes per selection.
export const PAPER_SIZES = [
    { key: 'A0', label: 'A0 — 841 × 1189 mm', wMm: 841, hMm: 1189 },
    { key: 'A1', label: 'A1 — 594 × 841 mm', wMm: 594, hMm: 841 },
    { key: 'A2', label: 'A2 — 420 × 594 mm', wMm: 420, hMm: 594 },
    { key: 'A3', label: 'A3 — 297 × 420 mm', wMm: 297, hMm: 420 },
    { key: 'A4', label: 'A4 — 210 × 297 mm', wMm: 210, hMm: 297 },
    { key: 'Letter', label: 'Letter — 8.5 × 11 in', wMm: 215.9, hMm: 279.4 },
    { key: 'Legal', label: 'Legal — 8.5 × 14 in', wMm: 215.9, hMm: 355.6 },
    { key: 'Tabloid', label: 'Tabloid — 11 × 17 in', wMm: 279.4, hMm: 431.8 },
];
export const getPaperSize = (key) => { var _a; return (_a = PAPER_SIZES.find((p) => p.key === key)) !== null && _a !== void 0 ? _a : PAPER_SIZES[0]; };
// Sheet dimensions in mm for the given orientation.
export const sheetDimsMm = (p, o) => o === 'portrait' ? { wMm: p.wMm, hMm: p.hMm } : { wMm: p.hMm, hMm: p.wMm };
// Fit the sheet into the container (preserving paper aspect), then carve out the
// Option-B sub-rects: outer margins, the map window, and the bottom title block.
export const computeSheetLayout = (containerW, containerH, p, o, opts = {}) => {
    var _a, _b, _c, _d;
    const padding = (_a = opts.padding) !== null && _a !== void 0 ? _a : 48;
    const marginFrac = (_b = opts.marginFrac) !== null && _b !== void 0 ? _b : 0.035;
    const bandFrac = (_c = opts.bandFrac) !== null && _c !== void 0 ? _c : 0.16;
    const gapFrac = (_d = opts.gapFrac) !== null && _d !== void 0 ? _d : 0.02;
    if (containerW <= 0 || containerH <= 0)
        return null;
    const { wMm, hMm } = sheetDimsMm(p, o);
    const aspect = wMm / hMm;
    const availW = Math.max(1, containerW - padding * 2);
    const availH = Math.max(1, containerH - padding * 2);
    let sheetW = availW;
    let sheetH = sheetW / aspect;
    if (sheetH > availH) {
        sheetH = availH;
        sheetW = sheetH * aspect;
    }
    const sheetX = (containerW - sheetW) / 2;
    const sheetY = (containerH - sheetH) / 2;
    const sheet = { x: sheetX, y: sheetY, width: sheetW, height: sheetH };
    const margin = marginFrac * Math.min(sheetW, sheetH);
    const band = bandFrac * sheetH;
    const gap = gapFrac * sheetH;
    const contentX = sheetX + margin;
    const contentY = sheetY + margin;
    const contentW = sheetW - margin * 2;
    const contentH = sheetH - margin * 2 - band - gap;
    let mapW = contentW;
    let legend;
    if (opts.legend) {
        const legendW = Math.min(Math.max(contentW * 0.22, 150), 260);
        const colGap = margin * 0.6;
        mapW = Math.max(1, contentW - legendW - colGap);
        legend = { x: contentX + mapW + colGap, y: contentY, width: legendW, height: contentH };
    }
    const mapWindow = { x: contentX, y: contentY, width: mapW, height: contentH };
    const titleBlock = {
        x: contentX,
        y: sheetY + sheetH - margin - band,
        width: contentW,
        height: band,
    };
    return { sheet, mapWindow, titleBlock, legend };
};
